import classicText from "./Classic.SC2Hotkeys" with { type: "text" };
import gridText from "./Grid.SC2Hotkeys" with { type: "text" };
import gridLeftiesText from "./GridLefties.SC2Hotkeys" with { type: "text" };
import standardText from "./Standard.SC2Hotkeys" with { type: "text" };
import standardLeftiesText from "./StandardLefties.SC2Hotkeys" with { type: "text" };

/**
 * The StarCraft II "Standard" (Blizzard default) hotkey table, as a canonical US-Qwerty
 * .SC2Hotkeys text. A real `.SC2Hotkeys` export is a *delta*
 * against an in-engine base preset: commands the player never rebinds are simply absent, and the
 * game fills them from Standard. Keylens had no such base, so a sparse upload rendered almost empty.
 * This is that base - the full Standard table, so a sparse upload can be filled to the setup the
 * player actually plays (see ingestSC2's fillDefaults).
 *
 * Sourced by extracting the game's own data (not fabricated, not the design mock - the same bar as
 * TheCore and the Destiny cvars): the `Button/Hotkey/*` and `UI/Hotkey/*` default tables in every
 * `mods/<m>.sc2mod/enus.sc2data/LocalizedData/GameHotkeys.txt`, preferring the `_USD` (US Default =
 * Standard) column over the base value, excluding the other preset columns (`_USDL/_NRS/_GLS/_GRS/_SC1`).
 * Control groups and the other engine-level globals live only in the campaign/multiplayer mod tables
 * (never in core.sc2mod), and were cross-verified against Liquipedia. Generated by
 * tools/generate.py from SC2 build 5.0.16.97364; see PROVENANCE.md.
 */
export const STANDARD_DEFAULTS_TEXT: string = standardText;

/** The Blizzard "Classic" (SC1-style) stock table, extracted like Standard but preferring the `_SC1`
 * column; 145 commands differ from Standard. Both a base preset (for `Suffix=_SC1` uploads) and a
 * selectable stock Profile. */
export const CLASSIC_DEFAULTS_TEXT: string = classicText;

/** The Blizzard "Standard for Lefties" stock table, extracted like Standard but preferring the `_NRS`
 * column (the base the game's own "Standard for Lefties" preset is a delta against): a left-handed
 * positional mirror of Standard. Both the ~102 global keys (control groups, camera, ArmySelect
 * F2 -> F5, IdleWorker F1 -> Backspace) and the ~281 abilities that sit on a left-hand key
 * (Attack A -> K, Stop S -> O) relocate to the right hand; keys already right-of-centre stay
 * (Move M, Stimpack T). Both a base preset (for `Suffix=_NRS` uploads) and a selectable stock Profile. */
export const STANDARD_LEFTIES_TEXT: string = standardLeftiesText;

/** The Blizzard "Grid" stock table. Grid is *positional* - the engine derives each ability's key from
 * the cell it occupies on that unit's command card times a 15-cell template (QWERT/ASDFG/ZXCVB), so
 * there is no flat ability->key column to pull (the game's `_GLS` ability column is empty). This table
 * is the projection of every card position keylens renders, resolved per unit and per Context (a
 * command that grids differently across rosters carries a `key/<roster>/<ctx>` row that the
 * context-aware ingest prefers). Generated by tools/generate-grid.py; see PROVENANCE.md. */
export const GRID_DEFAULTS_TEXT: string = gridText;

/** The Blizzard "Grid for Lefties" table: the same card positions through the `_GRS` template
 * (UIOP[/JKL;'/NM,./, the right-hand keys) with the lefty global relocation (ArmySelect F2 -> F5). */
export const GRID_LEFTIES_TEXT: string = gridLeftiesText;

/**
 * The base preset a `.SC2Hotkeys` file is a delta against, keyed by its `[Settings] Suffix=` value:
 * `""` (absent) = Standard, `_SC1` = Classic, `_NRS` = Standard for Lefties, `_GLS` = Grid,
 * `_GRS` = Grid for Lefties. All vendored real game data. A `Suffix=_GLS` upload now fills from the
 * Grid base (its positional card keys) instead of falling back to Standard.
 */
export const SC2_BASE_PRESETS: Record<string, string> = {
  "": standardText,
  _SC1: classicText,
  _NRS: standardLeftiesText,
  _GLS: gridText,
  _GRS: gridLeftiesText,
};
